import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Newspaper, Book } from 'lucide-react';
import { UnifiedContent } from '../../types/content';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { scrollToTop } from '../../utils/scroll';

interface ContentCardProps {
  content: UnifiedContent;
}

const stripHtmlTags = (html: string): string => {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
};

export function ContentCard({ content }: ContentCardProps) {
  const handleClick = () => {
    // Scroll to top before navigating
    scrollToTop();
  };

  const getTypeIcon = () => {
    return content.type === 'news' ? (
      <Newspaper className="w-4 h-4 text-white drop-shadow" />
    ) : (
      <Book className="w-4 h-4 text-white drop-shadow" />
    );
  };

  const getTypeLabel = () => {
    return content.type === 'news' ? 'Actualité' : 'Dossier';
  };

  return (
    <Link
      to={`/${content.type === 'news' ? 'news' : 'dossiers'}/${content.id}`}
      onClick={handleClick}
      className="group relative bg-white dark:bg-gray-800 rounded-2xl shadow-md hover:shadow-xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden transition-all duration-300 transform hover:-translate-y-2"
    >
      <div className="relative aspect-[16/9] overflow-hidden">
        <img
          src={content.cover || content.thumbnail}
          alt={content.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            if (!target.src.includes('fallback')) {
              target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?q=80&w=2670&auto=format&fit=crop';
            }
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

        <div className="absolute top-3 left-3">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-500/90 to-cyan-500/90 backdrop-blur-md rounded-full shadow-lg">
            {getTypeIcon()}
            <span className="text-xs font-semibold text-white drop-shadow">
              {getTypeLabel()}
            </span>
          </div>
        </div>

        <div className="absolute bottom-3 left-3 right-3">
          <h2 className="text-base sm:text-lg font-bold text-white line-clamp-2 leading-snug sm:leading-snug drop-shadow-lg">
            {content.title}
          </h2>
        </div>
      </div>

      <div className="p-5">
        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-4 leading-relaxed">
          {stripHtmlTags(content.description)}
        </p>

        <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700/50">
          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 font-medium">
            <Calendar className="w-3.5 h-3.5 mr-1.5" />
            <span>
              {format(parseISO(content.created), 'dd MMM yyyy', { locale: fr })}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <img
              src={content.author.avatar || `https://ui-avatars.com/api/?name=${content.author.firstname}&background=random`}
              alt={content.author.firstname}
              className="w-6 h-6 rounded-full ring-2 ring-gray-100 dark:ring-gray-700"
            />
            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
              {content.author.firstname}
            </span>
          </div>
        </div>
      </div>

      {/* Hover effect overlay */}
      <div className="absolute inset-0 ring-2 ring-blue-500/0 group-hover:ring-blue-500/50 rounded-2xl transition-all duration-300 pointer-events-none"></div>
    </Link>
  );
}